import React from 'react'
import { TradingHourDay } from '@scentregroup/shared/graphql'
import { BasicFeature } from '@scentregroup/shared/hub-components/basic-feature'
import { isMemberExclusive } from '@scentregroup/shared/helpers/is-member-exclusive'
import { getEventOccurrences } from '@scentregroup/shared/helpers/get-event-details'
import useCentreTime, {
  CentreTime,
} from '@scentregroup/shared/helpers/use-centre-time'
import {
  getOccurrences,
  type Occurrences,
} from '@scentregroup/shared/helpers/occurrences-v2'
import { SGPageTemplate } from '@scentregroup/shared/types/page-templates'
import { CFCallToAction } from '@scentregroup/shared/types/contentful'
import { Template } from '@scentregroup/shared/components/page-templates/template'
import { ContentDetail } from '../../../lib/get-content-detail'
import { Centre } from '../../../lib/get-centre-by-slug'
import { createRetailerUtilityfromStore } from '../../../lib/createRetailerUtilityfromStore'

function convertOccurences(
  { startAt, endAt, hideAt, occurrencesCollection }: ContentDetail['event'],
  timeZone: CentreTime | null
): Occurrences | null {
  const occurrences = occurrencesCollection?.items.filter(item => !!item) || []

  if (!occurrences.length) {
    return null
  }

  return getOccurrences(
    false,
    { startAt, endAt, hideAt, occurrences },
    timeZone
  )
}

export function EventContentDetail({
  template,
  content,
  centre,
  tradingHours,
}: {
  template: SGPageTemplate
  content: ContentDetail['event']
  centre: Centre
  tradingHours: TradingHourDay[]
}): JSX.Element {
  const centreTime = useCentreTime()
  const {
    title,
    bodyCopy: body,
    priceDescription: price,
    highlightAttribute,
    storesCollection,
  } = content
  const termsAndConditions = content?.termsAndConditions || undefined
  const supportingColor = content?.heroImageSet?.masterSupportingColour
  const heroImage = content?.heroImageSet?.master?.url
  const occurrences = convertOccurences(content, centreTime)
  const callsToAction = (content?.callsToActionCollection?.items ||
    []) as CFCallToAction[]

  const store = content?.storesCollection?.featuredStoreForCentre

  const retailerUtility = createRetailerUtilityfromStore(
    store,
    tradingHours,
    centre.timeZone,
    centre.slug
  )

  const memberExclusive = isMemberExclusive(highlightAttribute)

  const geoLocation = {
    latitude: centre.latitude,
    longitude: centre.longitude,
  }

  const storeEntry = storesCollection?.items.find(
    store => store?.centre?.sys.id === centre.sys.id
  )

  const storePhysicalAddress = storeEntry?.physicalAddress ?? ''
  const nearestLocation = storeEntry?.nearestLocation ?? ''

  return (
    <>
      <BasicFeature
        title={title || ''}
        shortDescription="*Upcoming events are subject to change"
        body={body || ''}
        supportingColor={supportingColor}
        heroImage={heroImage}
        timeZone={centre?.timeZone}
        termsAndConditions={termsAndConditions}
        retailerUtility={retailerUtility}
        memberExclusive={memberExclusive}
        eventDetail={
          occurrences
            ? {
                geoLocation,
                nearestLocation,
                price: price ?? '',
                physicalAddress: storePhysicalAddress,
                centreTimeZone: centre.timeZone,
                upcoming: getEventOccurrences(
                  occurrences?.times || [],
                  centreTime
                ),
                pastOccurrences: getEventOccurrences(
                  occurrences?.pastTimes || [],
                  centreTime
                ),
                callsToAction,
                shareable: !memberExclusive,
              }
            : undefined
        }
      />
      <Template template={template} />
    </>
  )
}
